import { useMemo } from 'react';
import { TextProps } from 'react-native';
import {
  CustomTagRendererRecord,
  DomVisitorCallbacks,
  MixedStyleDeclaration,
  MixedStyleRecord,
  RenderHTML,
} from 'react-native-render-html';
import sanitize from 'sanitize-html';
import theme from 'shared/styles/theme';
import { VaroFontNames } from 'shared/styles/typography';

export type { DomVisitorCallbacks, MixedStyleDeclaration, MixedStyleRecord };

const systemFonts = [
  VaroFontNames.VaroHand,
  VaroFontNames.National2Compressed,
  VaroFontNames.NeueHaasGroteskDisplayProMediumNormal,
  VaroFontNames.NeueHaasGroteskDisplayProRomanNormal,
];

const baseStyle: MixedStyleDeclaration = {
  fontFamily: VaroFontNames.NeueHaasGroteskDisplayProRomanNormal,
  fontSize: theme.typography.body.base.fontSize,
  lineHeight: theme.typography.body.base.lineHeight,
  letterSpacing: theme.typography.body.base.letterSpacing,
  color: theme.color.text.primary,
};

const defaultTagsStyles: Readonly<Record<string, MixedStyleDeclaration>> = {
  b: {
    ...(theme.typography.title.md as MixedStyleDeclaration),
    marginBottom: theme.spacing.s,
    marginTop: theme.spacing.m,
  },
  li: {
    ...(theme.typography.body.base as MixedStyleDeclaration),
    marginBottom: theme.spacing.s,
    paddingLeft: theme.spacing.xs,
  },
  p: {
    ...(theme.typography.body.base as MixedStyleDeclaration),
    marginBottom: theme.spacing.m,
  },
  ul: {
    paddingLeft: theme.spacing.m,
  },
};

export interface HTMLRendererProps {
  htmlAsString: string;
  contentWidth?: number;
  renderers?: CustomTagRendererRecord;
  tagsStyles?: Readonly<Record<string, MixedStyleDeclaration>>;
  renderersProps?: Record<string, unknown>;
  defaultTextProps?: TextProps;
  domVisitors?: DomVisitorCallbacks;
  classesStyles?: MixedStyleRecord;
  customBaseStyle?: MixedStyleDeclaration;
}

export const HTMLRenderer = ({
  htmlAsString,
  contentWidth,
  renderers,
  tagsStyles,
  renderersProps,
  defaultTextProps,
  domVisitors,
  classesStyles,
  customBaseStyle,
}: HTMLRendererProps) => {
  const clean: string = sanitize(htmlAsString);
  const source = useMemo(() => ({ html: clean }), [clean]);
  const mergedBaseStyle = useMemo(
    () => ({ ...baseStyle, ...customBaseStyle }),
    [customBaseStyle],
  );
  const mergedTagsStyles = useMemo(
    () => ({ ...defaultTagsStyles, ...tagsStyles }),
    [tagsStyles],
  );
  return (
    <RenderHTML
      defaultTextProps={defaultTextProps}
      contentWidth={contentWidth}
      source={source}
      renderers={renderers}
      tagsStyles={mergedTagsStyles}
      renderersProps={renderersProps}
      systemFonts={systemFonts}
      domVisitors={domVisitors}
      classesStyles={classesStyles}
      baseStyle={mergedBaseStyle}
    />
  );
};
